import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../../services/accountingApi';

function DayBook() {
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadDayBook();
    }, [fromDate, toDate]);

    const loadDayBook = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('DayBook: Fetching with params', { from_date: fromDate, to_date: toDate });
            const response = await accountingApi.getDayBook({
                from_date: fromDate,
                to_date: toDate
            });
            console.log('DayBook: API Response', response);

            if (response.success) {
                setVouchers(response.data || []);
            } else {
                setError(response.message || 'Failed to load day book');
            }
        } catch (err) {
            console.error('Error loading day book:', err);
            setError('An error occurred while loading the day book');
        } finally {
            setLoading(false);
        }
    };

    // Group vouchers by date
    const vouchersByDate = vouchers.reduce((acc, voucher) => {
        const date = voucher.voucher_date;
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(voucher);
        return acc;
    }, {});

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">Day Book</h1>
                </div>
                <p className="text-sm text-slate-600">Daily transaction register</p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>
                </div>
            </div>

            {loading && (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-2 text-slate-600">Loading day book...</p>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6">
                    <p className="text-sm text-rose-700">{error}</p>
                </div>
            )}

            {!loading && vouchers.length === 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                    <p className="text-slate-400 italic">No vouchers found in this period</p>
                </div>
            )}

            {!loading && Object.keys(vouchersByDate).length > 0 && (
                <div className="space-y-6">
                    {Object.keys(vouchersByDate).sort((a, b) => new Date(b) - new Date(a)).map(date => (
                        <div key={date} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="bg-indigo-50 border-b border-indigo-100 px-6 py-3">
                                <h3 className="font-bold text-indigo-900">{new Date(date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h3>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {vouchersByDate[date].map(voucher => (
                                    <div key={voucher.id} className="p-6 hover:bg-slate-50 transition-colors">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <Link to={`/accounting/vouchers/${voucher.id}`} className="text-lg font-bold text-indigo-600 hover:text-indigo-800">
                                                    {voucher.voucher_number}
                                                </Link>
                                                <p className="text-sm text-slate-500">{voucher.voucher_type}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-slate-900">₹{(voucher.total_debit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${voucher.status === 'Posted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {voucher.status}
                                                </span>
                                            </div>
                                        </div>
                                        {voucher.narration && (
                                            <p className="text-sm text-slate-600 mb-3 italic">{voucher.narration}</p>
                                        )}
                                        {voucher.entries && voucher.entries.length > 0 && (
                                            <div className="bg-slate-50 rounded-lg p-4">
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="text-xs text-slate-500 uppercase">
                                                            <th className="text-left pb-2">Account</th>
                                                            <th className="text-right pb-2">Debit</th>
                                                            <th className="text-right pb-2">Credit</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-200">
                                                        {voucher.entries.map((entry, idx) => (
                                                            <tr key={idx}>
                                                                <td className="py-2 text-slate-700">{entry.account_group}</td>
                                                                <td className="py-2 text-right font-medium text-slate-900">
                                                                    {parseFloat(entry.debit_amount) > 0 ? `₹${parseFloat(entry.debit_amount).toLocaleString()}` : '-'}
                                                                </td>
                                                                <td className="py-2 text-right font-medium text-slate-900">
                                                                    {parseFloat(entry.credit_amount) > 0 ? `₹${parseFloat(entry.credit_amount).toLocaleString()}` : '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default DayBook;
